import { useAppDispatch } from "../../../app/hooks";
import { setSelectedTime, useAllTaus, useSelectedTime, } from "../../../features/surface/surfaceSlice";
import { ProgressSlider } from "./TimelineSliderStyle";
import { setIsPlaying } from "../../../features/time/playbackSlice";
import { useMaxDisplayTime, useMinDisplayTime } from "../../../features/time/TimelineSlice";
import { useEffect, useRef, useState } from "react";
import { isBrowserSafariMobile } from "../../../features/surface/windowUtils";
import { RiskViewerKind, useRiskViewerStateKind } from "../../../features/riskViewer/riskViewerSlice";

type TimelineSliderProps = {
    isRiskError: boolean,
    displayTime: number,
};

/**
 * TimelineSlider component.
 * @property {TimelineSliderProps} props - Props for the TimelineSlider component.
 * @returns {JSX.Element} The TimelineSlider component.
 */
export default function TimelineSlider( {
    isRiskError,
    displayTime,
}: Readonly<TimelineSliderProps> )
{

    const dispatch = useAppDispatch( );
    const taus = useAllTaus();
    const currentSurfaceTime = useSelectedTime();
    const minDisplayTime = useMinDisplayTime();
    const maxDisplayTime = useMaxDisplayTime();
    const riskViewerStateKind = useRiskViewerStateKind();
    const [ currentlySelectedTime, setCurrentlySelectedTime ] = useState<number>( displayTime );
    const dispatchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>( null );

    function dispatchUpdate( selectedTime: number )
    {
        dispatch( setIsPlaying( false ) );
        dispatch( setSelectedTime( selectedTime ) );
    }

    useEffect( () =>
    {
        if ( riskViewerStateKind === RiskViewerKind.TRIP_STATE )
        {
            setCurrentlySelectedTime( currentSurfaceTime );
        }
        else
        {
            setCurrentlySelectedTime( displayTime );
        }
    }, [ displayTime ] );

    /**
     * Handles the change event on the slider.
     * @param {Event} event - The event triggered by the slider change.
     * @param {number | Array<number>} value - The new value of the slider.
     */
    const handleSliderChange = ( event: Event, value: number | Array<number> ) =>
    {
        if ( isBrowserSafariMobile( ) && event.type === "mousedown" )
        {
            return;
        }
        const selectedTime = typeof value === "number" ? value : value[0];
        setCurrentlySelectedTime( selectedTime );
        if ( dispatchTimeoutRef.current )
        {
            clearTimeout( dispatchTimeoutRef.current );
        }

        dispatchTimeoutRef.current = setTimeout( () =>
        {
            dispatchUpdate( selectedTime );
        }, 5 );
    };


    return (
        <div style = {{ overflow: "hidden" }} id = "step5">
            {riskViewerStateKind === RiskViewerKind.TRIP_STATE ? (
                <ProgressSlider
                    themecolor = "currentColor"
                    hasError = {isRiskError}
                    railPct = {100}
                    trackPct = {100}
                    min = {minDisplayTime}
                    max = {maxDisplayTime}
                    value = {currentlySelectedTime}
                    valueLabelDisplay = "off"
                    shiftStep = {30000} // 30 seconds in ms
                    onChange = {handleSliderChange}
                />
            ) : (
                <ProgressSlider
                    themecolor = "currentColor"
                    hasError = {isRiskError}
                    railPct = {100}
                    trackPct = {100}
                    marks = {taus.map( t => ( { value: t } ) )}
                    step = {null}
                    min = {minDisplayTime}
                    max = {maxDisplayTime}
                    value = {currentlySelectedTime}
                    valueLabelDisplay = "off"
                    onChange = {handleSliderChange}
                    disabled = {taus.length === 0}
                />
            )}
        </div>
    );
}
